import {useWindowSize} from "../hooks/common/useWindowSize";
import MainLayout from "../layouts/MainLayout";
import Toolbox from "../components/dashboard/Toolbox";
import {useEffect, useState} from "react";
import GridLayout from "react-grid-layout";
import {deepCopy} from "../utils/common";
import {Draggable, DraggableDashboardWrapper, DraggableItem, ToolboxBtn} from "../styles/dashboard";
import ModalLayout from "../layouts/ModalLayout";
import {Background, CFlex, CMargin, DText, Flex} from "../styles/MarketStyles";
import Text from "../utils/Text";
import Input from "../components/common/input/Input";
import {Decoration} from "../styles/CommonStyles";
import {useQueryContext} from "../contexts/query";
import {useLayoutContext} from "../contexts/layout";
import {loadPersistedData, persistData} from "../utils/persistor";
import {useLocation, useNavigate} from "react-router-dom";
import {initialToolboxItems, layoutComps} from "../constants/layouts";
import {ReactComponent as NoCustomLayout} from "../assets/images/no-custom-layout.svg";


const initialOptions = {
    fullscreen: false, layoutName: ''
}

const DraggableDashboard = () => {

    const navigate = useNavigate()
    const search = new URLSearchParams(useLocation().search)
    const editMode = search.get('mode') === 'edit'

    const { width } = useWindowSize()
    const { setToast } = useQueryContext()
    const {
        setLayout: setMainLayout,
        setActiveLayout,
        refreshAvailableLayouts,
        deleteLayout
    } = useLayoutContext()

    const [ layout, setLayout ] = useState([])
    const [ toolboxItems, setToolboxItems ] = useState(initialToolboxItems)
    const [options, setOptions] = useState(initialOptions)
    const [saveModal, setSaveModal] = useState(false)

    useEffect(() => {
        const layout = search?.get('layout')
        if (layout) {
            const layouts = loadPersistedData('layout')
            const savedLayout = layouts[layout]
            if (!savedLayout) {
                return
            }
            const newToolboxItems = deepCopy(toolboxItems)
            savedLayout.forEach(item => {
                const toolboxItem = newToolboxItems.find(x => x.name === item.i)
                toolboxItem.active = true
            })
            setToolboxItems(newToolboxItems)
            setLayout(savedLayout)

            const fullscreens = loadPersistedData('fullscreen')
            const isFullscreen = fullscreens.includes(layout)

            setOptions({ fullscreen: isFullscreen, layoutName: layout })
        }
    }, [])

    const onToolboxStateChange = (idx) => {

        // changing toolbox item state
        const newItems = deepCopy(toolboxItems)
        newItems[idx].active = !newItems[idx].active
        setToolboxItems(newItems)

        const {name, active, x, y, w, h, minW, minH} = newItems[idx]

        // changing layout state
        if (active) {
            const layoutItem = {i: name, x, y, w, h, minW, minH}
            const newLayout = deepCopy(layout)
            newLayout.push(layoutItem)
            setLayout(newLayout)
        }
    }

    const onLayoutChange = (layout) => {
        setLayout(layout)
    }

    const onOptionsChange = (value, type) => {
        const newOptions = deepCopy(options)
        newOptions[type] = value
        setOptions(newOptions)
    }

    const onOpenSaveModal = () => {
        if (layout.length) setSaveModal(true)
        else setToast({
            message: 'layout-least-component',
            show: true, isError: true
        })
    }

    const onSaveLayout = () => {
        if (!options.layoutName) {
            setToast({
                message: 'fill-inputs-error',
                show: true, isError: true
            })
            return
        }

        /**
         * layout name unique check
         */
        const layoutNames = Object.keys(loadPersistedData('layout'))
        if (layoutNames.includes(options.layoutName) && !editMode) {
            setToast({
                message: 'invalid-layout-name', isError: true, show: true
            })
            return
        }

        /**
         * edit mode
         */
        if (editMode) {
            const editLayoutName = search.get('layout')
            const layoutName = options.layoutName
            if (layoutNames.includes(options.layoutName)) {
                setToast({
                    message: 'invalid-layout-name', isError: true, show: true
                })
                return
            }
            if (editLayoutName !== layoutName) {
                deleteLayout(editLayoutName)
            }else {
                const fullscreen = loadPersistedData('fullscreen')
                const wasFullscreen = fullscreen.includes(editLayoutName)
                if (wasFullscreen && !options.fullscreen) {
                    const newFullscreen = fullscreen.filter(x => x !== editLayoutName)
                    persistData('fullscreen', newFullscreen)
                }
            }


        }

        /**
         * persisting new layout
         */
        const layouts = loadPersistedData('layout')
        const newLayouts = deepCopy(layouts)
        newLayouts[options.layoutName] = layout.map(item => {
            const defaultIndex = initialToolboxItems.findIndex(x => x.name === item.i)
            let defaultZIndex = 101
            if (defaultIndex > -1) {
                defaultZIndex = initialToolboxItems[defaultIndex].zIndex
            }
            return {...item, zIndex: defaultZIndex}
        })
        persistData('layout', newLayouts)

        /**
         * handle fullscreen
         */
        const fullscreen = loadPersistedData('fullscreen')
        if (options.fullscreen) {
            try {
                if (!fullscreen.includes(options.layoutName)) {
                    fullscreen.push(options.layoutName)
                    persistData('fullscreen', fullscreen)
                }
            }catch (err) { }
        }

        /**
         * setting new layout as active and refresh available layouts
         */
        setActiveLayout(options.layoutName)
        setMainLayout(layout)
        refreshAvailableLayouts()

        navigate('/')
    }

    const onReset = () => {
        setOptions(initialOptions)
        setToolboxItems(initialToolboxItems)
        setLayout([])
    }

    const onCancel = () => {
        onReset()
        navigate('/')
    }

    return (
        <MainLayout>
            {width > 1300 ?
                <>
                    <Toolbox
                        items={toolboxItems}
                        onStateChange={onToolboxStateChange}
                        options={options}
                        onOptionsChange={onOptionsChange}
                        onSave={onOpenSaveModal}
                        onCancel={onCancel}
                        onReset={onReset}
                    />
                    <DraggableDashboardWrapper
                        border={layout.length}
                        fullscreen={options.fullscreen}
                    >
                        <GridLayout
                            className="layout"
                            layout={layout}
                            onLayoutChange={onLayoutChange}
                            cols={24}
                            rowHeight={10}
                            width={(width <= 1500 || options.fullscreen) ? width : 1500}
                            margin={[0, 0]}
                            draggableHandle={'.draggable'}
                        >
                            {toolboxItems.map((item, idx) => {
                                const Pane = layoutComps[item.name]
                                if (!item.active) return <></>
                                return (
                                    <div key={item.name}>
                                        <DraggableItem item={item.name}>
                                            <Draggable className={'draggable'} />
                                            <Pane />
                                        </DraggableItem>
                                    </div>
                                )
                            })}
                        </GridLayout>
                    </DraggableDashboardWrapper>
                </>
                :
                <CFlex fw height={'480px'}>
                    <NoCustomLayout width={'300px'} height={'300px'} />
                    <Flex>
                        <Decoration style={{ margin: '0 5px' }} />
                        <DText primary fontSize={'m'}>
                            <Text tid={'layout-min-size-note'} />
                        </DText>
                    </Flex>
                </CFlex>

            }

            <ModalLayout
                open={saveModal}
                onClose={() => setSaveModal(false)}
                width={'380px'}
            >
                <CFlex fw align={'flex-start'}>
                        <DText primary fontSize={'m'}>
                            <Text tid={'layout-name'} />
                        </DText>
                        <CMargin margin={'8px'} />
                        <Flex>
                            <Decoration />
                            <DText style={{ margin: '0 6px' }} main fontSize={'ss'}>
                                <Text tid={'layout-name-note'} />
                            </DText>
                        </Flex>
                        <Background bg={'primaryBg'}>
                            <Input
                                label={'name'}
                                value={options.layoutName}
                                onInputChange={(v) => onOptionsChange(v, 'layoutName')}
                                size={'small'}
                            />
                        </Background>

                        <Flex fw justify={'space-around'}>
                            <ToolboxBtn submit style={{ margin: '0' }} onClick={onSaveLayout}>
                                <Text tid={'save'}/>
                            </ToolboxBtn>
                            <ToolboxBtn cancel style={{ margin: '0' }} onClick={() => setSaveModal(false)}>
                                <Text tid={'cancel'}/>
                            </ToolboxBtn>
                        </Flex>
                </CFlex>
            </ModalLayout>
        </MainLayout >
    )
}

export default DraggableDashboard;



